/** Mirrored from core-platform-backend domain values. */

export const OrganizationType = {
    Lender: 'lender',
    Insurer: 'insurer',
    ServiceProvider: 'service_provider',
    Other: 'other',
} as const;
export type OrganizationType = (typeof OrganizationType)[keyof typeof OrganizationType];
export const ORGANIZATION_TYPE_VALUES: readonly OrganizationType[] =
    Object.values(OrganizationType);

export const CaseStatus = {
    New: 'new',
    Active: 'active',
    Delinquent: 'delinquent',
    Recovery: 'recovery',
    Closed: 'closed',
} as const;
export type CaseStatus = (typeof CaseStatus)[keyof typeof CaseStatus];
export const CASE_STATUS_VALUES: readonly CaseStatus[] = Object.values(CaseStatus);

export const CasePriority = {
    High: 'high',
    Medium: 'medium',
    Low: 'low',
} as const;
export type CasePriority = (typeof CasePriority)[keyof typeof CasePriority];
export const CASE_PRIORITY_VALUES: readonly CasePriority[] = Object.values(CasePriority);

export const CaseType = {
    Loan: 'loan',
    ProductPayment: 'product_payment',
    Insurance: 'insurance',
    AccountUpgrade: 'account_upgrade',
    Monitoring: 'monitoring',
} as const;
export type CaseType = (typeof CaseType)[keyof typeof CaseType];
export const CASE_TYPE_VALUES: readonly CaseType[] = Object.values(CaseType);

export const SubjectType = {
    Individual: 'individual',
    Business: 'business',
} as const;
export type SubjectType = (typeof SubjectType)[keyof typeof SubjectType];
export const SUBJECT_TYPE_VALUES: readonly SubjectType[] = Object.values(SubjectType);

export const PoiType = {
    Residence: 'residence',
    BusinessAddress: 'business_address',
    RegisteredAddress: 'registered_address',
    AssetLocation: 'asset_location',
    Other: 'other',
} as const;
export type PoiType = (typeof PoiType)[keyof typeof PoiType];
export const POI_TYPE_VALUES: readonly PoiType[] = Object.values(PoiType);

export const VisitStatus = {
    Available: 'available',
    Assigned: 'assigned',
    PendingApproval: 'pending_approval',
    Flagged: 'flagged',
    Approved: 'approved',
    Rejected: 'rejected',
} as const;
export type VisitStatus = (typeof VisitStatus)[keyof typeof VisitStatus];
export const VISIT_STATUS_VALUES: readonly VisitStatus[] = Object.values(VisitStatus);

/** Report `workflowType` values — aligned with visit types (see migration ETL). */
export const REPORT_WORKFLOW_TYPE_VALUES = ['verification', 'recovery'] as const;

export const REPORT_SUBMISSION_FILTER_VALUES = ['draft', 'submitted'] as const;

/** Admin-only visit transitions (PATCH /visits/:id/status). */
export const VISIT_ADMIN_TRANSITION_STATUSES: readonly VisitStatus[] = [
    VisitStatus.Approved,
    VisitStatus.Rejected,
];

/**
 * A visit can only be (re)assigned to a field officer before the work is done.
 * Once it is pending approval, approved, rejected, or flagged, assignment no
 * longer applies.
 */
export const VISIT_ASSIGNABLE_STATUSES: ReadonlySet<string> = new Set([
    VisitStatus.Available,
    VisitStatus.Assigned,
]);

export const CasePoiRole = {
    Primary: 'primary',
    VisitTarget: 'visit_target',
} as const;

export const MonitoringFrequency = {
    BiWeekly: 'bi_weekly',
    Monthly: 'monthly',
    Quarterly: 'quarterly',
    Custom: 'custom',
} as const;
export type MonitoringFrequency =
    (typeof MonitoringFrequency)[keyof typeof MonitoringFrequency];
export const MONITORING_FREQUENCY_VALUES: readonly MonitoringFrequency[] =
    Object.values(MonitoringFrequency);

export const MonitoringIntervalUnit = {
    Days: 'days',
    Weeks: 'weeks',
    Months: 'months',
} as const;
export type MonitoringIntervalUnit =
    (typeof MonitoringIntervalUnit)[keyof typeof MonitoringIntervalUnit];
export const MONITORING_INTERVAL_UNIT_VALUES: readonly MonitoringIntervalUnit[] =
    Object.values(MonitoringIntervalUnit);
